
import { useEffect, useState } from "react";
import { useIndexedDB } from "react-indexed-db-hook";
import { useReactMediaRecorder } from "react-media-recorder";
import Dropzone, { IFileWithMeta, IStyleCustomization, StatusValue } from "react-dropzone-uploader";
import { authClient } from "../utils/amplifyClient";
import { Button as MUIButton } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { yellow } from "@mui/material/colors";
import {
    Badge as CSbadge,
    Box,
    Button,
    Checkbox,
    Container,
    Form,
    FormField,
    Grid,
    Header,
    Icon,
    Input,
    ProgressBar,
    Select,
    Textarea,
} from './legacy-widgets';
import { InputMode } from "@aws-amplify/ui-react";
import { useNetworkState } from "@uidotdev/usehooks";
import * as Sentry from "@sentry/browser";
import { LoadingSpinner } from "./LoadingSpinner";
import {
    getForm as getFormQuery,
    listFormByCompanyId as listFormByCompanyIdQuery,
} from "../graphql/queries";

import { useUserContext } from "../contexts/UserContext";
import { useFormsContext } from "../contexts/FormsContext";
import type { Form as FormType } from "../types/schema";
import {
    DEFAULT_FORM_INPUT_TYPES,
    INPUT_MODES,
    ITEM_STATUS_CODES,
    LOCAL_STORAGE_KEYS,
    LOCATION_TYPES,
} from "../utils/constants";

import { tFormPrompts, tGPSInput, tGPSPosition, tInspectionFormInput, tMediaInput } from "../types";

import "react-dropzone-uploader/dist/styles.css";
import { Key } from "react-indexed-db-hook/lib/indexed-db";

import { ShiftReportReview } from "./ShiftReportReview";
import { Stack } from "./ui";

const shiftReportNamePrefix = (shiftType: 'start-of-day' | 'end-of-day') =>
    shiftType === 'end-of-day' ? 'end of day' : 'start of day';

const formMatchesShiftReportName = (
    form: Pick<FormType, 'id' | 'name'>,
    formName: string,
    formId: string | undefined,
    shiftType: 'start-of-day' | 'end-of-day',
) => {
    if (formId && form.id === formId) {
        return true;
    }
    const normalizedFormName = form.name?.toLowerCase().trim() ?? '';
    const normalizedTarget = formName.toLowerCase().trim();
    const shiftPrefix = shiftReportNamePrefix(shiftType);
    return (
        normalizedFormName.startsWith(normalizedTarget) ||
        normalizedTarget.startsWith(normalizedFormName) ||
        normalizedFormName.startsWith(shiftPrefix)
    );
};

const parseShiftFormPrompts = (formData: string | null | undefined): tInspectionFormInput[] => {
    const prompts: tInspectionFormInput[] = [];
    if (!formData) {
        return prompts;
    }

    JSON.parse(formData).forEach((formDataSection: tFormPrompts) => {
        if (formDataSection.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion) {
            prompts.push({
                type: DEFAULT_FORM_INPUT_TYPES.standAloneQuestion,
                label: formDataSection.prompt,
                inputMode: formDataSection.inputMode?.value || "text",
                description: "",
                value: "",
                notes: "",
                photo: [],
                title: "",
            });
        } else {
            formDataSection.prompts?.length &&
                formDataSection.prompts.forEach((prompt) => {
                    prompts.push({
                        type: DEFAULT_FORM_INPUT_TYPES.section,
                        title: formDataSection.name,
                        label: prompt.component,
                        description: prompt.description,
                        inputMode: prompt.inputMode?.value || "text",
                        subType:
                            prompt.type === 'question'
                                ? DEFAULT_FORM_INPUT_TYPES.fourResponse
                                : prompt.type,
                        value: "",
                        notes: "",
                        photo: [],
                        options: prompt.options || [],
                        selectedOptions: [],
                    });
                });
        }
    });

    return prompts;
};

const theme = createTheme({
    palette: {
        secondary: yellow,
    },
});

export const ShiftReportWizard = ({
    onInspectionComplete,
    onFinalSubmit,
    isSending,
    formName = "start of day",
    formId: formIdProp,
    shiftType = "start-of-day",
}: {
    onInspectionComplete?: () => void;
    onFinalSubmit?: () => void | Promise<void>;
    isSending?: boolean;
    formName?: string;
    formId?: string;
    shiftType?: 'start-of-day' | 'end-of-day';
}) => {
    const network = useNetworkState();
    const { company, companyOwner, companyUser } = useUserContext();
    const { companyForms } = useFormsContext();
    const [activeStepIndex, setActiveStepIndex] = useState<number>(0); // --> Current step of the wizard
    const [_isLoading, setIsLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isLocating, setIsLocating] = useState(false);
    const [userLocation, setUserLocation] = useState<tGPSInput | null>(null);
    const [shiftFormPrompts, setShiftFormPrompts] = useState<tInspectionFormInput[]>([]);
    const [media, setMedia] = useState<{ [key: number]: tMediaInput[] } | null>(null);
    // const [mediaAction, setMediaAction] = useState<tMediaAction | null>(null);
    const [mediaQueue, setMediaQueue] = useState<{
        fileWithMeta: IFileWithMeta;
        status: StatusValue;
    }[]>([]);
    const [shouldReviewForm, setShouldReviewForm] = useState(false);
    const [audioReview, setAudioReview] = useState<{ [key: number]: Blob }>({});
    const [formId, setFormId] = useState<string | null>(formIdProp ?? null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const { add, getByID, deleteRecord } = useIndexedDB("media");

    const companyId =
        (company?.id as string) ||
        (companyUser?.companyId as string) ||
        companyOwner?.companyId;

    useEffect(() => {
        const loadShiftForm = async () => {
            if (shiftFormPrompts.length) return;

            setIsLoading(true);
            setIsPageLoading(true);
            setLoadError(null);

            try {
                let shiftForm: FormType | null = null;

                if (formIdProp) {
                    shiftForm =
                        companyForms.find((form) => form.id === formIdProp) ?? null;
                }

                if (!shiftForm && formIdProp && network.online) {
                    const { data } = await authClient.graphql({
                        query: getFormQuery,
                        variables: { id: formIdProp },
                    });
                    shiftForm = (data?.getForm as FormType | null) ?? null;
                }

                if (!shiftForm && network.online && companyId) {
                    const shiftFormResponse = await authClient.graphql({
                        query: listFormByCompanyIdQuery,
                        variables: { companyId },
                    });
                    const items = shiftFormResponse?.data?.listFormByCompanyId?.items ?? [];
                    shiftForm =
                        items.find((form) =>
                            formMatchesShiftReportName(form, formName, formIdProp, shiftType),
                        ) ?? null;
                }

                if (!shiftForm && !network.online && formIdProp) {
                    shiftForm =
                        companyForms.find((form) => form.id === formIdProp) ?? null;
                }

                if (!shiftForm) {
                    setLoadError(
                        network.online
                            ? 'Could not load this shift report form. Check that the form is published and try again.'
                            : 'This shift report form is not available offline. Connect to the internet and try again.',
                    );
                    return;
                }

                setFormId(shiftForm.id ?? formIdProp ?? null);
                const prompts = parseShiftFormPrompts(shiftForm.formData);
                if (prompts.length === 0) {
                    setLoadError('This shift report form has no questions configured yet.');
                    return;
                }
                setShiftFormPrompts(prompts);
            } catch (error) {
                console.error('Error loading form:', error);
                Sentry.captureException(error);
                setLoadError('Something went wrong while loading this report. Please try again.');
            } finally {
                setIsLoading(false);
                setIsPageLoading(false);
            }
        };

        loadShiftForm();
    }, [
        network.online,
        companyId,
        shiftFormPrompts.length,
        formName,
        formIdProp,
        shiftType,
        companyForms,
    ]);

    const getMimeType = () => {
        const types = [
            'audio/webm',
            'audio/mp4',
            'audio/ogg',
            'audio/wav'
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }

        // If none of the preferred MIME types are supported, 
        // let the browser use its default
        return '';
    };

    const { clearBlobUrl, status, startRecording, stopRecording } =
        useReactMediaRecorder({
            mediaRecorderOptions: {
                mimeType: getMimeType(),
                //switched from audio/wav to fix MIME errors - if this causes a problem can go back
            },
            audio: true,
            onStop: async (_url, blob) => {
                const obj = {
                    name: "audio.wav",
                    file: blob,
                    question: activeStepIndex,
                    formId: formId,
                };
                const event = await add(obj);
                setAudioReview({
                    ...audioReview,
                    [activeStepIndex]: blob,
                });
                setMedia((prev) => {
                    return {
                        ...prev,
                        [activeStepIndex]: (prev && prev[activeStepIndex])
                            ? [
                                ...prev[activeStepIndex],
                                {
                                    id: event,
                                    name: "audio.wav",
                                },
                            ]
                            : [
                                {
                                    id: event,
                                    name: "audio.wav",
                                },
                            ],
                    };
                });
            },
        });

    const coordinates =
        JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.LOCATION) || '{}') || {};

    useEffect(() => {
        setLocationFunction();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const storePosition = (position: tGPSPosition) => {
            // hasPosition = true;
            setUserLocation({
                type: LOCATION_TYPES.gps,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            });
            localStorage.setItem(
                LOCAL_STORAGE_KEYS.LOCATION,
                JSON.stringify({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                })
            );
            setIsLocating(false);
        };

        if (isLocating) {
            navigator.geolocation.getCurrentPosition(
                storePosition,
                () => {
                    setUserLocation({
                        type: coordinates?.latitude
                            ? LOCATION_TYPES.lastKnownPosition
                            : LOCATION_TYPES.unknown,
                        ...coordinates,
                    });
                    setIsLocating(false);
                },
                { timeout: 10000 }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLocating]);

    useEffect(() => {
        const processMediaQueue = async () => {
            if (mediaQueue.length === 0) return;

            const [currentMedia, ...remainingQueue] = mediaQueue;

            if (currentMedia.status === "removed") {
                if (!media) return;

                const recordToDelete = media[activeStepIndex]?.find(
                    (el) => el.name === currentMedia.fileWithMeta.file.name
                )?.id;

                if (recordToDelete) {
                    await deleteRecord(recordToDelete);
                    setMedia((prev) => ({
                        ...prev,
                        [activeStepIndex]: media[activeStepIndex].filter(
                            (el) => el.name !== currentMedia.fileWithMeta.file.name
                        ),
                    }));
                }
            } else if (currentMedia.status === "done") {
                try {
                    const obj = {
                        name: currentMedia.fileWithMeta.file.name,
                        file: currentMedia.fileWithMeta.file,
                        question: activeStepIndex,
                        formId: formId,
                    };
                    const event = await add(obj);
                    const photoData = await getByID(event);

                    setMedia((prev) => ({
                        ...prev,
                        [activeStepIndex]: (prev && prev[activeStepIndex])
                            ? [
                                ...prev[activeStepIndex],
                                {
                                    id: event,
                                    name: currentMedia.fileWithMeta.file.name,
                                    data: photoData,
                                },
                            ]
                            : [
                                {
                                    id: event,
                                    name: currentMedia.fileWithMeta.file.name,
                                    data: photoData,
                                },
                            ],
                    }));
                } catch (error) {
                    console.log(error);
                    Sentry.captureException(error);
                }
            }

            setMediaQueue(remainingQueue);
        };

        processMediaQueue();
    }, [mediaQueue, activeStepIndex, media, formId, add, getByID, deleteRecord]);

    const setLocationFunction = async () => {
        if (navigator.geolocation) {
            setIsLocating(true);
        } else {
            if (coordinates?.latitude) {
                setUserLocation({
                    type: LOCATION_TYPES.lastKnownPosition,
                    ...coordinates,
                });
            }
        }
    };

    const handleChangeStatus = (fileWithMeta: IFileWithMeta, dropZoneStatus: StatusValue) => {
        if (dropZoneStatus === "removed" || dropZoneStatus === "done") {
            setMediaQueue(prev => [...prev, {
                fileWithMeta,
                status: dropZoneStatus
            }]);
        }
    };
    const isAudioRecordingStopped = (stateOfRecorder: string) => {
        return stateOfRecorder === "stopped" || stateOfRecorder === "idle";
    };

    const getLocationStatusColor = () => {
        switch (userLocation?.type) {
            case LOCATION_TYPES.gps:
                return "green";
            case LOCATION_TYPES.lastKnownPosition:
                return "blue";
            case null:
            default:
                return "grey";
        }
    };

    return (
        <LoadingSpinner
            isLoading={isPageLoading}
            content={
                loadError ? (
                    <Stack size="m">
                        <Box variant="p">{loadError}</Box>
                        <Button variant="primary" onClick={() => onInspectionComplete?.()}>
                            Close
                        </Button>
                    </Stack>
                ) : shiftFormPrompts.length > 0 ? (
                    <ThemeProvider theme={theme}>
                        <Grid
                            gridDefinition={[
                                { colspan: { default: 12 } },
                                { colspan: { default: 12 } },
                            ]}
                        >
                            {companyUser?.name && (
                                <Header
                                    description={
                                        <CSbadge color={`${getLocationStatusColor()}`}>
                                            Location:{" "}
                                            {userLocation?.type || "Getting coordinates from device..."}
                                        </CSbadge>
                                    }
                                    variant="h1"
                                >{`${companyUser.name}: ${shiftType === 'end-of-day' ? 'End Of Day' : 'Start Of Day'} Report`}</Header>
                            )}

                        </Grid>
                        {shouldReviewForm && (
                            <ShiftReportReview
                                cancel={() => setShouldReviewForm(false)}
                                shiftFormPrompts={shiftFormPrompts}
                                userLocation={userLocation}
                                media={media}
                                onFinalSubmit={onFinalSubmit}
                                isSending={isSending}
                                formId={formId}
                                shiftType={shiftType}
                            />
                        )}
                        {!shouldReviewForm && (
                            <>
                                <Stack size="l">
                                    <Box color="text-status-success" margin={{ top: "l" }}>
                                        Progress
                                        <ProgressBar
                                            value={
                                                (activeStepIndex / shiftFormPrompts.length) * 100 || 0
                                            }
                                        />
                                    </Box>
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                        }}
                                    >
                                        <Form
                                            actions={
                                                <Stack direction="horizontal" size="m">
                                                    {activeStepIndex > 0 && (
                                                        <Button
                                                            formAction="none"
                                                            variant="normal"
                                                            onClick={() => {
                                                                clearBlobUrl();
                                                                setActiveStepIndex(activeStepIndex - 1);
                                                            }}
                                                        >
                                                            Previous
                                                        </Button>
                                                    )}
                                                    <Button
                                                        formAction="none"
                                                        variant="primary"
                                                        disabled={ // Next button should be disabled if... 
                                                            shiftFormPrompts[activeStepIndex]?.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
                                                            shiftFormPrompts[activeStepIndex]?.subType !== DEFAULT_FORM_INPUT_TYPES.dropdown &&
                                                            !shiftFormPrompts[activeStepIndex]?.value
                                                        }
                                                        onClick={() => {
                                                            clearBlobUrl();
                                                            if (media && media[activeStepIndex]) {
                                                                media[activeStepIndex].forEach(async (mediaItem) => {
                                                                    if (!shiftFormPrompts[activeStepIndex]?.photo?.includes(mediaItem)) {
                                                                        shiftFormPrompts[activeStepIndex]?.photo?.push(mediaItem)
                                                                    }
                                                                });
                                                            }

                                                            // Only advance if NOT on the last question
                                                            if (activeStepIndex < shiftFormPrompts.length - 1) {
                                                                setActiveStepIndex(activeStepIndex + 1);
                                                            } else {
                                                                // On last question, only go to review when button is clicked
                                                                setShouldReviewForm(true);
                                                            }
                                                        }}
                                                    >
                                                        {activeStepIndex === shiftFormPrompts.length - 1 ? "Review and Sign" : "Next"}
                                                    </Button>
                                                </Stack>
                                            }
                                        >

                                            {shiftFormPrompts.map((prompt, idx) => {
                                                if (idx !== activeStepIndex) {
                                                    return null;
                                                }
                                                return (
                                                    <Box
                                                        key={prompt.label}
                                                        display={idx === activeStepIndex ? "block" : "none"}
                                                    >

                                                        {prompt.type === DEFAULT_FORM_INPUT_TYPES.standAloneQuestion && (
                                                            <Container>
                                                                <FormField
                                                                    label={prompt.label}
                                                                    description={prompt.description}
                                                                >
                                                                    {prompt.inputMode !== INPUT_MODES.TEXTAREA && (
                                                                        <Input
                                                                            autoFocus
                                                                            value={prompt.value}
                                                                            onChange={({ detail }) => {
                                                                                const updatePrompts = [...shiftFormPrompts];
                                                                                updatePrompts[idx].value = detail.value;
                                                                                setShiftFormPrompts(updatePrompts);
                                                                            }}
                                                                            inputMode={prompt.inputMode as InputMode | undefined}
                                                                            type={prompt.inputMode === INPUT_MODES.NUMBER ? "number" : "text"}
                                                                        />
                                                                    )}
                                                                    {prompt.inputMode === INPUT_MODES.TEXTAREA && (
                                                                        <Textarea
                                                                            autoFocus
                                                                            value={prompt.value}
                                                                            onChange={({ detail }) => {
                                                                                const updatePrompts = [...shiftFormPrompts];
                                                                                updatePrompts[idx].value = detail.value;
                                                                                setShiftFormPrompts(updatePrompts);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </FormField>
                                                            </Container>
                                                        )}
                                                        {prompt.type === DEFAULT_FORM_INPUT_TYPES.section && (
                                                            <>
                                                                <Container
                                                                    header={<Header variant="h2">{prompt.title}</Header>}
                                                                >
                                                                    <Stack direction="vertical" size="xxs">
                                                                        {prompt.subType === (DEFAULT_FORM_INPUT_TYPES.fourResponse || DEFAULT_FORM_INPUT_TYPES.dropdown) &&
                                                                            <Box variant="h5">What you are inspecting:</Box>
                                                                        }
                                                                        <div className="flash">
                                                                            <h3>{prompt.label}</h3>
                                                                        </div>
                                                                        {prompt.description && (
                                                                            <>
                                                                                {prompt.subType !== DEFAULT_FORM_INPUT_TYPES.checkbox &&
                                                                                    <Box variant="p">What you are looking for:</Box>}
                                                                                <div className="flash">
                                                                                    <p>{prompt.description}</p>
                                                                                </div>
                                                                            </>
                                                                        )
                                                                        }

                                                                        {prompt.subType === DEFAULT_FORM_INPUT_TYPES.yesNo && (
                                                                            <Grid
                                                                                gridDefinition={[
                                                                                    { colspan: { "default": 6, s: 3 } },
                                                                                    { colspan: { "default": 6, s: 3 } },
                                                                                    { colspan: { "default": 6, s: 3 } },
                                                                                    { colspan: { "default": 6, s: 3 } }
                                                                                ]}
                                                                            >
                                                                                <MUIButton
                                                                                    variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.ok ? "contained" : "outlined"}
                                                                                    color="success"
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        fontSize: 40,
                                                                                        padding: "20px 10px",
                                                                                        width: "100%",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...shiftFormPrompts];
                                                                                        updatePrompts[idx].value = ITEM_STATUS_CODES.ok;
                                                                                        setShiftFormPrompts(updatePrompts);

                                                                                        // Only auto-advance if NOT the last question
                                                                                        if (activeStepIndex < shiftFormPrompts.length - 1) {
                                                                                            setActiveStepIndex(activeStepIndex + 1);
                                                                                        }
                                                                                        // On last question, do nothing - let user click "Review and Sign"
                                                                                    }}
                                                                                >
                                                                                    Yes
                                                                                </MUIButton>
                                                                                <MUIButton
                                                                                    variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.warn ? "contained" : "outlined"}
                                                                                    color="secondary"
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        fontSize: 40,
                                                                                        padding: "20px 10px",
                                                                                        width: "100%",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...shiftFormPrompts];
                                                                                        updatePrompts[idx].value = ITEM_STATUS_CODES.warn;
                                                                                        setShiftFormPrompts(updatePrompts);
                                                                                    }}
                                                                                >
                                                                                    No
                                                                                </MUIButton>
                                                                            </Grid>
                                                                        )}

                                                                        {prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox && (
                                                                            <Stack direction="vertical" size="xs">
                                                                                {prompt.options?.map((option: string, optionIdx: number) => (
                                                                                    <Checkbox
                                                                                        key={optionIdx}
                                                                                        checked={prompt.selectedOptions?.includes(option) || false}
                                                                                        onChange={({ detail }) => {
                                                                                            const updatePrompts = [...shiftFormPrompts];
                                                                                            const currentSelected = updatePrompts[idx].selectedOptions || [];

                                                                                            if (detail.checked) {
                                                                                                updatePrompts[idx].selectedOptions = [...currentSelected, option];
                                                                                            } else {
                                                                                                updatePrompts[idx].selectedOptions = currentSelected.filter((val: string) => val !== option);
                                                                                            }
                                                                                            setShiftFormPrompts(updatePrompts);
                                                                                        }}
                                                                                    >
                                                                                        {option}
                                                                                    </Checkbox>
                                                                                ))}
                                                                            </Stack>
                                                                        )}

                                                                        {prompt.subType === DEFAULT_FORM_INPUT_TYPES.sectionQuestion && (
                                                                            <>
                                                                                <Container>
                                                                                    {prompt.inputMode !== INPUT_MODES.TEXTAREA && (
                                                                                        <Input
                                                                                            autoFocus
                                                                                            value={prompt.value}
                                                                                            onChange={({ detail }) => {
                                                                                                const updatePrompts = [...shiftFormPrompts];
                                                                                                updatePrompts[idx].value = detail.value;
                                                                                                setShiftFormPrompts(updatePrompts);
                                                                                            }}
                                                                                            inputMode={prompt.inputMode as InputMode | undefined}
                                                                                            type={prompt.inputMode === INPUT_MODES.NUMBER ? "number" : "text"}
                                                                                        />
                                                                                    )}
                                                                                    {prompt.inputMode === INPUT_MODES.TEXTAREA && (
                                                                                        <Textarea
                                                                                            autoFocus
                                                                                            value={prompt.value}
                                                                                            onChange={({ detail }) => {
                                                                                                const updatePrompts = [...shiftFormPrompts];
                                                                                                updatePrompts[idx].value = detail.value;
                                                                                                setShiftFormPrompts(updatePrompts);
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </Container>
                                                                            </>
                                                                        )}

                                                                        {prompt.subType === DEFAULT_FORM_INPUT_TYPES.dropdown && (
                                                                            <Select
                                                                                selectedOption={prompt.value ? { label: prompt.value, value: prompt.value } : null}
                                                                                onChange={({ detail }) => {
                                                                                    const updatePrompts = [...shiftFormPrompts];
                                                                                    updatePrompts[idx].value = detail.selectedOption.value ?? '';
                                                                                    setShiftFormPrompts(updatePrompts);
                                                                                }}
                                                                                options={prompt.options?.map((option: string) => ({
                                                                                    label: option, // Use string as both label and value
                                                                                    value: option
                                                                                })) || []}
                                                                                placeholder="Select an option"
                                                                                expandToViewport
                                                                            />
                                                                        )}
                                                                        {(!prompt.subType || prompt.subType === DEFAULT_FORM_INPUT_TYPES.fourResponse) && (
                                                                            <Grid
                                                                                gridDefinition={[
                                                                                    { colspan: { "default": 6, s: 3 } },
                                                                                    { colspan: { "default": 6, s: 3 } },
                                                                                    { colspan: { "default": 6, s: 3 } },
                                                                                    { colspan: { "default": 6, s: 3 } }
                                                                                ]}
                                                                            >
                                                                                <MUIButton
                                                                                    variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.ok ? "contained" : "outlined"}
                                                                                    color="success"
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        fontSize: 40,
                                                                                        padding: "20px 10px",
                                                                                        width: "100%",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...shiftFormPrompts];
                                                                                        updatePrompts[idx].value = ITEM_STATUS_CODES.ok;
                                                                                        setShiftFormPrompts(updatePrompts);
                                                                                        activeStepIndex < shiftFormPrompts.length - 1
                                                                                            ? setActiveStepIndex(activeStepIndex + 1)
                                                                                            : setShouldReviewForm(true);
                                                                                    }}
                                                                                >
                                                                                    <Icon name="status-positive" size="medium" />
                                                                                </MUIButton>
                                                                                <MUIButton
                                                                                    variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.warn ? "contained" : "outlined"}
                                                                                    color="secondary"
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        fontSize: 40,
                                                                                        padding: "20px 10px",
                                                                                        width: "100%",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...shiftFormPrompts];
                                                                                        updatePrompts[idx].value = ITEM_STATUS_CODES.warn;
                                                                                        setShiftFormPrompts(updatePrompts);
                                                                                    }}
                                                                                >
                                                                                    <Icon name="status-warning" size="medium" />

                                                                                </MUIButton>
                                                                                <MUIButton
                                                                                    variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.inop ? "contained" : "outlined"}
                                                                                    color="error"
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        padding: "20px 10px",
                                                                                        fontSize: 40,
                                                                                        width: "100%",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...shiftFormPrompts];
                                                                                        updatePrompts[idx].value = ITEM_STATUS_CODES.inop;
                                                                                        setShiftFormPrompts(updatePrompts);
                                                                                    }}
                                                                                >
                                                                                    <Icon name="status-negative" size="medium" />
                                                                                </MUIButton>
                                                                                <MUIButton
                                                                                    variant={!prompt.value || prompt.value === ITEM_STATUS_CODES.na ? "contained" : "outlined"}
                                                                                    color={"primary"}
                                                                                    style={{
                                                                                        borderRadius: 20,
                                                                                        fontSize: '14px',
                                                                                        padding: "20px 10px",
                                                                                        width: "100%",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                        const updatePrompts = [...shiftFormPrompts];
                                                                                        updatePrompts[idx].value = ITEM_STATUS_CODES.na;
                                                                                        activeStepIndex < shiftFormPrompts.length - 1
                                                                                            ? setActiveStepIndex(activeStepIndex + 1)
                                                                                            : setShouldReviewForm(true);

                                                                                        setShiftFormPrompts(updatePrompts);
                                                                                    }}
                                                                                >
                                                                                    N/A
                                                                                </MUIButton>
                                                                            </Grid>
                                                                        )}

                                                                        {shiftFormPrompts[idx].value &&
                                                                            shiftFormPrompts[idx].value !== ITEM_STATUS_CODES.ok &&
                                                                            (prompt.subType === DEFAULT_FORM_INPUT_TYPES.fourResponse ||
                                                                                prompt.subType === DEFAULT_FORM_INPUT_TYPES.yesNo || prompt.subType === DEFAULT_FORM_INPUT_TYPES.checkbox ||
                                                                                !prompt.subType) && (
                                                                                <>
                                                                                    <FormField label="Notes">
                                                                                        <Textarea
                                                                                            value={prompt.notes || ""}
                                                                                            onChange={({ detail }) => {
                                                                                                const updatePrompts = [...shiftFormPrompts];
                                                                                                updatePrompts[idx].notes = detail.value;
                                                                                                setShiftFormPrompts(updatePrompts);
                                                                                            }}
                                                                                        />
                                                                                    </FormField>
                                                                                    <Container header={<h2>Add Audio Note</h2>}>
                                                                                        <Stack direction="horizontal" size="xxl">
                                                                                            {/* Audio recording functionality remains unchanged */}
                                                                                            {isAudioRecordingStopped(status) && (
                                                                                                <Button
                                                                                                    onClick={startRecording}
                                                                                                    iconAlign="left"
                                                                                                    iconName="microphone"
                                                                                                >
                                                                                                    Record
                                                                                                </Button>
                                                                                            )}
                                                                                            {!isAudioRecordingStopped(status) && (
                                                                                                <Button
                                                                                                    onClick={stopRecording}
                                                                                                    loading={status === "acquiring_media"}
                                                                                                    iconAlign="left"
                                                                                                    iconName="microphone-off"
                                                                                                >
                                                                                                    {status === "recording" && <>End Recording</>}
                                                                                                    {status === "acquiring_media" && <>Starting</>}
                                                                                                </Button>
                                                                                            )}
                                                                                            {audioReview[activeStepIndex] && (
                                                                                                <>
                                                                                                    <audio
                                                                                                        src={URL.createObjectURL(audioReview[activeStepIndex])}
                                                                                                        controls
                                                                                                    >
                                                                                                        <source
                                                                                                            src={URL.createObjectURL(audioReview[activeStepIndex])}
                                                                                                            type="audio/wav"
                                                                                                        />
                                                                                                    </audio>
                                                                                                    <Button
                                                                                                        iconAlign="left"
                                                                                                        iconName="close"
                                                                                                        onClick={() => {
                                                                                                            const newMedia = { ...media };
                                                                                                            delete newMedia[activeStepIndex];
                                                                                                            setMedia(newMedia);
                                                                                                            const newAudioReview = { ...audioReview };
                                                                                                            delete newAudioReview[activeStepIndex];
                                                                                                            setAudioReview(newAudioReview);
                                                                                                        }}
                                                                                                    >
                                                                                                        Remove
                                                                                                    </Button>
                                                                                                </>
                                                                                            )}
                                                                                        </Stack>
                                                                                    </Container>
                                                                                    <Dropzone
                                                                                        addClassNames={'video-dropzone' as IStyleCustomization<string>}
                                                                                        onChangeStatus={handleChangeStatus}
                                                                                        accept="image/jpeg, image/jpg, image/png, audio/wav, audio/mp3, video/*"
                                                                                        inputContent={(_files, extra) => {
                                                                                            if (extra.reject) {
                                                                                                return "Image or video files only";
                                                                                            }
                                                                                            return "Click to add images or videos";
                                                                                        }}
                                                                                    />
                                                                                </>
                                                                            )}
                                                                        <Box>
                                                                            <Stack direction="horizontal" size="l">
                                                                                {prompt?.photo?.map((photo, index) => {
                                                                                    return (
                                                                                        <div
                                                                                            key={index}
                                                                                            className="photo-container"
                                                                                        >
                                                                                            <img
                                                                                                key={index}
                                                                                                src={URL.createObjectURL(photo?.data?.file as unknown as MediaSource)}
                                                                                                alt={prompt.label}
                                                                                                className="photo"
                                                                                            />
                                                                                            <div className="delete-image">
                                                                                                <Button
                                                                                                    variant="icon"
                                                                                                    iconName="close"
                                                                                                    onClick={async () => {
                                                                                                        await deleteRecord(photo?.id as Key)
                                                                                                        const updatedFormPrompts = [
                                                                                                            ...shiftFormPrompts,
                                                                                                        ];
                                                                                                        updatedFormPrompts[
                                                                                                            idx
                                                                                                        ].photo?.splice(index, 1);
                                                                                                        if (media) {
                                                                                                            setMedia((prev) => {
                                                                                                                return {
                                                                                                                    ...prev,
                                                                                                                    [activeStepIndex]: media[activeStepIndex].filter(
                                                                                                                        (el) => el.id !== photo?.id
                                                                                                                    ),
                                                                                                                };
                                                                                                            });
                                                                                                        }
                                                                                                        setShiftFormPrompts(
                                                                                                            updatedFormPrompts
                                                                                                        );
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        </div>
                                                                                    )
                                                                                })}
                                                                            </Stack>
                                                                        </Box>
                                                                    </Stack>
                                                                </Container>
                                                            </>
                                                        )}

                                                    </Box>
                                                );
                                            })}
                                        </Form>
                                    </form>
                                </Stack>
                            </>
                        )}
                    </ThemeProvider>
                ) : <div />
            }
        />
    );
};
